/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Modal from "@mui/material/Modal";
import PropTypes from "prop-types";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import CardTitle from "components/CardTitle";
import { useTranslation } from "react-i18next";
import { useMaterialUIController } from "context";

// Material Dashboard 2 PRO React examples
import DataTable from "components/DataTable";
import ApartmentForm from "layouts/project/components/apartmentForm";
import { connect } from "react-redux";
import { CellActive, isReservationActive } from "layouts/clients/clientReservationsUtils";

import { updateClientReservations } from "store/actions/clientReservationsActions";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import Footer from "components/Footer";
import helpers from "../../analytics/AnalyticsHelpers";
// import InvoiceForm from "layouts/project/components/invoiceForm";

const customNameSort = (rowA, rowB, columnId, desc) => {
  const a = String(rowA.values[columnId] || "");
  const b = String(rowB.values[columnId] || "");

  const result = helpers.naturalStringSort(a, b);

  return desc ? -result : result;
};

const columns = (isProjectScoped) => {
  const { t } = useTranslation();
  const columnsToShow = [
    {
      Header: t("activeReservation"),
      accessor: "active",
      Cell: CellActive,
      maxWidth: 15,
    },
    {
      Header: t("name"),
      accessor: "name",
      width: 20,
      isSorted: true,
      sortType: customNameSort,
    },
    {
      Header: t("onnaDevice"),
      accessor: "onnaDeviceName",
      width: 20,
    },
  ];
  if (!isProjectScoped) {
    columnsToShow.splice(1, 0, {
      Header: t("project"),
      accessor: "projectName",
      width: 20,
    });
  }
  return columnsToShow;
};

function AlertDialog({ open, handleClose, handleAgree }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { t } = useTranslation();
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <MDBox
        sx={({ palette: { white, background } }) => ({
          backgroundColor: darkMode ? background.sidenav : white.main,
        })}
      >
        <DialogTitle id="alert-dialog-title">{t("confirmation")}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">{t("stepNotUndo")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleClose}>{t("cancel")}</MDButton>
          <MDButton onClick={handleAgree} autoFocus>
            {t("OK")}
          </MDButton>
        </DialogActions>
      </MDBox>
    </Dialog>
  );
}

function Apartments({
  database,
  projectId,
  apartmentsData,
  onnaDevicesData,
  projectsData,
  clientReservationsObj,
  menuConfig,
  actGetClientReservations,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  // const [openInvoice, setOpenInvoice] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [uids, setUids] = useState([]);
  const [actionType, setActionType] = useState("");

  useEffect(() => {
    actGetClientReservations();
  }, [actGetClientReservations]);

  const isProjectScoped = Boolean(projectId);

  const projectApartments = projectsData?.[projectId]?.apartments || {};

  const apartmentsSource = isProjectScoped
    ? Object.values(apartmentsData || {}).reduce((acc, apartment) => {
        const apartmentId = apartment?.apartmentId;
        if (apartmentId && Object.prototype.hasOwnProperty.call(projectApartments, apartmentId)) {
          acc[apartmentId] = apartment;
        }
        return acc;
      }, {})
    : apartmentsData;

  const onnaDevicesSource = onnaDevicesData;
  const clientReservations = Array.isArray(clientReservationsObj)
    ? clientReservationsObj
    : Object.values(clientReservationsObj || {}).flatMap((item) =>
        item && typeof item === "object" ? Object.values(item) : []
      );
  const now = Date.now();
  const activeApartmentIds = new Set(
    clientReservations
      .filter(
        (reservation) => reservation?.apartmentId && isReservationActive(reservation, null, now)
      )
      .map((reservation) => reservation.apartmentId)
  );

  const apartmentProjectMap = Object.entries(projectsData || {}).reduce(
    (acc, [currentProjectId, project]) => {
      Object.keys(project?.apartments || {}).forEach((apartmentId) => {
        acc[apartmentId] = currentProjectId;
      });
      return acc;
    },
    {}
  );

  const getProjectIdByApartment = (apartmentId) => apartmentProjectMap[apartmentId] || "";

  const formatedData = Object.values(apartmentsSource)
    .map((apartment) => {
      const { onnaId, apartmentId } = apartment;
      const apartmentProjectId = isProjectScoped ? projectId : getProjectIdByApartment(apartmentId);
      return {
        ...apartment,
        projectId: apartmentProjectId,
        projectName: projectsData?.[apartmentProjectId]?.name || apartmentProjectId,
        onnaDeviceName: onnaDevicesSource[onnaId]?.name || onnaId,
        active: activeApartmentIds.has(apartmentId),
      };
    })
    .sort((a, b) => {
      const nameA = String(a?.name || "");
      const nameB = String(b?.name || "");
      return helpers.naturalStringSort(nameA, nameB);
    });

  const onSubmit = (data) => {
    switch (actionType) {
      case "edit":
        database.editApartment(data);
        setOpen(false);
        break;
      case "add":
        database.addApartment(data, projectId);

        setOpen(false);
        break;
      default:
        break;
    }
  };

  const remove = (data) => {
    const dataToSend = data.map((row) => row.original);
    setActionType("remove");
    setUids(dataToSend);
    setOpenConfirm(true);
  };

  const confirm = async () => {
    switch (actionType) {
      case "remove":
        if (isProjectScoped) {
          await database.removeApartments(uids, projectId);
        } else {
          const apartmentsByProject = uids.reduce((acc, apartment) => {
            const apartmentProjectId =
              apartment?.projectId || getProjectIdByApartment(apartment?.apartmentId);
            if (!apartmentProjectId) return acc;
            if (!acc[apartmentProjectId]) acc[apartmentProjectId] = [];
            acc[apartmentProjectId].push(apartment);
            return acc;
          }, {});

          await Promise.all(
            Object.entries(apartmentsByProject).map(([projectUid, apartmentsToRemove]) =>
              database.removeApartments(apartmentsToRemove, projectUid)
            )
          );
        }
        setActionType("");
        setUids([]);
        setOpenConfirm(false);
        break;
      default:
        break;
    }
  };

  const add = () => {
    setActionType("add");
    setOpen(true);
  };

  const edit = (indexes) => {
    const apartmentIndexes = [];
    indexes.forEach((index) => {
      const {
        original: { apartmentId },
      } = index;
      apartmentIndexes.push(apartmentId);
    });
    setUids(apartmentIndexes);
    setActionType("edit");
    setOpen(true);
  };

  const apartmentsLabel = t(
    menuConfig?.apartments?.label ?? menuConfig?.apartments?.defaultLabel ?? "apartments"
  );

  const apartmentsContent = (
    <>
      <AlertDialog
        open={openConfirm}
        handleClose={() => {
          setOpenConfirm(false);
        }}
        handleAgree={() => {
          confirm();
        }}
      />
      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        style={{ overflow: "scroll" }}
      >
        <MDBox
          mt={3}
          sx={12}
          sm={8}
          style={{
            position: "absolute",
            width: "65vw",
            left: "50%",
            transform: "translate(-50%, 0%)",
          }}
        >
          <ApartmentForm onSubmit={onSubmit} actionType={actionType} uids={uids} />
        </MDBox>
      </Modal>
      {/* <Modal
        open={openInvoice}
        onClose={() => {
          setOpenInvoice(false);
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        style={{ overflow: "scroll" }}
      >
        <MDBox
          mt={3}
          sx={12}
          sm={8}
          style={{
            position: "absolute",
            width: "65vw",
            left: "50%",
            transform: "translate(-50%, 0%)",
          }}
        >
          <InvoiceForm onSubmit={onSubmitInvoice} uids={uids} />
        </MDBox>
      </Modal> */}
      <Card>
        <CardTitle title={apartmentsLabel} icon="house" bgColor="success" />
        <DataTable
          table={{
            columns: columns(isProjectScoped),
            rows: formatedData,
          }}
          canSearch
          canSelect
          canEdit
          canAdd={isProjectScoped}
          canActuate
          action={(data) => {
            const onnaId = data?.original?.onnaId;
            const apartmentId = data?.original?.apartmentId;
            const apartmentProjectId = data?.original?.projectId || projectId;
            if (onnaId && apartmentId && apartmentProjectId) {
              navigate(`/projects/${apartmentProjectId}/apartment/${onnaId}/${apartmentId}`);
            }
          }}
          actionIcon="arrow_forward_ios"
          actionColor="info"
          add={add}
          edit={edit}
          remove={remove}
        />
      </Card>
    </>
  );

  if (isProjectScoped) {
    return apartmentsContent;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar title={apartmentsLabel} route="/apartments" />
      <MDBox pt={4} pb={3}>
        {apartmentsContent}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

Apartments.defaultProps = {
  apartmentsData: {},
  onnaDevicesData: {},
  projectsData: {},
  clientReservationsObj: {},
  menuConfig: {},
  projectId: "",
  actGetClientReservations: () => {},
};

Apartments.propTypes = {
  apartmentsData: PropTypes.object,
  onnaDevicesData: PropTypes.object,
  projectsData: PropTypes.object,
  clientReservationsObj: PropTypes.object,
  menuConfig: PropTypes.object,
  projectId: PropTypes.string,
  actGetClientReservations: PropTypes.func,
  database: PropTypes.object.isRequired,
};

AlertDialog.defaultProps = {
  handleAgree: () => {},
};

AlertDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleAgree: PropTypes.func,
};

const mapStateToProps = (state) => {
  const {
    apartmentsReducer = {},
    onnaDevicesReducer = {},
    projectsReducer = {},
    mainUserReducer = {},
    clientReservationsReducer = {},
  } = state;

  const clientReservationsObj = clientReservationsReducer?.clientReservations || {};

  return {
    apartmentsData: apartmentsReducer || {},
    onnaDevicesData: onnaDevicesReducer || {},
    projectsData: projectsReducer || {},
    clientReservationsObj,
    menuConfig: mainUserReducer?.customApp?.menuConfig || {},
    isProjectManager: mainUserReducer?.isProjectManager || false,
    userProjects: mainUserReducer?.projects || {},
  };
};

const mapDispatchToProps = (dispatch) => ({
  actGetClientReservations: () => dispatch(updateClientReservations()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Apartments);
